<template>
    <div id="sawmachine-page">
        <div id="sawmachine-menu">
            <div
                class="timer"
                v-if="timer && !timer.finished"
            >
                <product-timer
                    :loading="loading"
                    @finish="onFinish"
                    @pause="onPause"
                    @unpause="startTimer(true)"
                    :saving="saving"
                    :pausing-or-restarting="pausingOrRestarting"
                    :timer="timer"
                    :all-finished="allFinished"
                />
            </div>
            <div class="titles">
                <div class="row">
                    <div class="paintlist-entry">
                        <ui5-label for="sawmachine-profile">
                            {{ trans('pages.sawmachine.scan_paintlist') }}
                        </ui5-label>
                        <ui5-input
                            type="Text"
                            :disabled="loading || (timer && !timer.finished)"
                            :value="paintlistId"
                            @input="paintlistId = $event.target.value"
                            @blur="loadData"
                            @keyup.enter="loadData"
                            :placeholder="
                                trans(
                                    'pages.sawmachine.scan_paintlist_placeholder'
                                )
                            "
                        />
                        <div class="camera-icon">
                            <ui5-button
                                @click="scan"
                                :disabled="
                                    loading || (timer && !timer.finished)
                                "
                            >
                                <i class="fa fa-camera fa-2x" />
                            </ui5-button>
                        </div>
                    </div>
                    <h3 v-if="paintlist">
                        {{ trans('pages.sawmachine.color') }}
                        {{ paintlist.ral }}
                    </h3>
                </div>
                <div class="row action-titles-row">
                    <h2>{{ trans('pages.sawmachine.paintlist_actions') }}</h2>
                    <h2>{{ trans('pages.sawmachine.sawline_actions') }}</h2>
                </div>
            </div>

            <div class="sawmachine-actions">
                <div class="left">
                    <div class="row">
                        <ui5-button
                            :disabled="!paintlist || printingProductionLabels"
                            @click="onPrintProductLabels"
                        >
                            <spinner v-if="printingProductionLabels" />
                            {{ trans('pages.sawmachine.pr_labels') }}
                        </ui5-button>
                    </div>
                    <div class="row">
                        <ui5-button
                            :disabled="!paintlist"
                            @click="onPrintVLRLabel"
                        >
                            {{ trans('pages.sawmachine.vlr_labels') }}
                        </ui5-button>
                    </div>
                </div>
                <div class="right">
                    <div class="row">
                        <ui5-button
                            :disabled="!paintlist || !paintlistrecord"
                            @click="onPrintVLRLabelForRecord"
                        >
                            {{
                                trans(
                                    'pages.sawmachine.vlr_labels_current_line'
                                )
                            }}
                        </ui5-button>
                        <ui5-button
                            :disabled="!paintlist || !paintlistrecord"
                            @click="onLineToSawmachine()"
                        >
                            {{
                                trans(
                                    'pages.sawmachine.current_line_to_sawmachine'
                                )
                            }}
                        </ui5-button>
                    </div>
                    <div class="row">
                        <ui5-button
                            :disabled="
                                !paintlist ||
                                !paintlistrecord ||
                                !selectedSawlineIds.length ||
                                reporting
                            "
                            @click="$refs.errorReportDialog.show()"
                        >
                            <div>
                                <spinner v-if="reporting" />
                                <template>
                                    {{ trans('pages.sawmachine.report_error') }}
                                </template>
                            </div>
                        </ui5-button>
                    </div>
                </div>
            </div>
        </div>
        <div class="tables">
            <table
                v-if="paintlist"
                cellspacing="0"
                cellpadding="0"
                id="profile-overview"
                class="modern-table hoverable"
            >
                <thead>
                    <tr class="product-table-header-tr">
                        <th class="product-table-header-td">
                            {{ trans('pages.sawmachine.image') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="left"
                        >
                            {{ trans('pages.sawmachine.description') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.sawmachine.length') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.sawmachine.surface_area') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.sawmachine.quantity') }}
                        </th>
                        <th
                            class="product-table-header-td"
                            align="center"
                        >
                            {{ trans('pages.sawmachine.finished') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-if="
                            !paintlist.records || paintlist.records.length == 0
                        "
                    >
                        <td colspan="6">
                            <li>{{ trans('pages.sawmachine.no_profiles') }}</li>
                        </td>
                    </tr>

                    <template v-else>
                        <tr
                            v-for="record in paintlist.records"
                            :id="'profile-row-' + record.ID"
                            :key="'record.' + record.ID"
                            class="product-table-item-tr"
                            :class="{
                                active:
                                    paintlistrecord &&
                                    paintlistrecord.paintlistrecordId ==
                                        record.ID,
                            }"
                            @click="showSawmachineSawlines(record.ID)"
                        >
                            <td
                                align="center"
                                class="img-cell"
                            >
                                <img
                                    :src="
                                        Config.url +
                                        '/profile/fetchImage/?profileId=' +
                                        record.profile
                                    "
                                    :alt="record.code"
                                />
                            </td>
                            <td align="left">
                                <div>
                                    <div class="paintlistbold">
                                        {{ record.description }}
                                    </div>
                                    <div class="paintlistdefault">
                                        {{ record.code }}
                                    </div>
                                    <div class="paintlistdefault">
                                        {{
                                            trans(
                                                'pages.sawmachine.profile_circumference'
                                            )
                                        }}
                                        {{ record.contour }}
                                    </div>
                                    <div class="paintlistitalic">
                                        {{ record.products }}
                                    </div>
                                    <div
                                        class="paintlistred"
                                        v-if="record.showPaintlist == 0"
                                    >
                                        {{
                                            trans(
                                                'pages.sawmachine.do_not_paint'
                                            )
                                        }}
                                    </div>
                                </div>
                            </td>
                            <td
                                align="center"
                                class="largerFont"
                            >
                                {{ record.length }} mm
                            </td>
                            <td align="center">{{ record.area }} m&#178;</td>
                            <td align="center">{{ record.count }}</td>
                            <td
                                align="center"
                                class="ball-cell"
                            >
                                <div
                                    :style="{
                                        'background-color':
                                            record.sawlinesSawn == 1
                                                ? 'green'
                                                : '',
                                    }"
                                    class="bol2"
                                />
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <div>
                <table
                    v-if="paintlistrecord"
                    id="sawlines-overview"
                    class="modern-table"
                    cellspacing="0"
                    cellpadding="0"
                >
                    <thead>
                        <tr class="product-table-header-tr">
                            <th
                                class="product-table-header-td"
                                align="center"
                            >
                                {{ trans('pages.sawmachine.selection') }}
                            </th>
                            <th
                                class="product-table-header-td"
                                align="center"
                            >
                                {{ trans('pages.sawmachine.description') }}
                            </th>
                            <th
                                class="product-table-header-td"
                                align="center"
                            >
                                {{ trans('pages.sawmachine.length') }}
                            </th>
                            <th
                                class="product-table-header-td"
                                align="center"
                            >
                                {{ trans('pages.sawmachine.miter') }}
                            </th>
                            <th
                                class="product-table-header-td"
                                align="center"
                            >
                                {{ trans('pages.sawmachine.finished') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <template
                            v-for="(sawline, i) in paintlistrecord.sawlist ||
                            []"
                        >
                            <tr
                                :id="'sawlines-row-' + sawline.ID"
                                class="product-table-item-tr"
                            ></tr>
                            <tr
                                :id="'sawlines-row-' + sawline.ID"
                                class="product-table-item-tr"
                            >
                                <td align="center">
                                    <ui5-checkbox
                                        :checked="sawline.selected"
                                        @change="
                                            sawline.selected =
                                                $event.target.checked
                                        "
                                    />
                                </td>
                                <td align="left">
                                    {{ sawline.partOfLength }} -
                                    {{ sawline.name }} - {{ sawline.productNr }}
                                </td>
                                <td
                                    align="left"
                                    class="largerFont"
                                >
                                    {{ sawline.value }}
                                </td>
                                <td align="center">{{ getMiter(sawline) }}</td>
                                <td align="center">
                                    <ui5-checkbox
                                        :checked="sawline.sawn == 1"
                                        @change="
                                            sawline.sawn = $event.target.checked
                                                ? '1'
                                                : '0';
                                            toggleSawmachineSawline(sawline);
                                        "
                                    />
                                </td>
                            </tr>
                            <!-- Show extra divide line -->
                            <template
                                v-if="
                                    i < paintlistrecord.sawlist.length - 1 &&
                                    sawline.partOfLength !==
                                        paintlistrecord.sawlist[i + 1]
                                            .partOfLength
                                "
                            >
                                <tr class="divider">
                                    <td colspan="4"></td>
                                </tr>
                            </template>
                        </template>
                    </tbody>
                </table>
                <spinner v-if="loadingSawlines" />
            </div>
        </div>
        <error-report-dialog
            ref="errorReportDialog"
            :reporting="reporting"
            @create="createErrorReport"
        />
    </div>
</template>
<script>
import Spinner from '../components/loader/Spinner';
import optimizedPaintListApi from '../api/optimizedPaintList';
import sawlistApi from '../api/sawlist';
import exportApi from '../api/export';
import sawlineApi from '../api/sawline';
import productionTimesApi from '../api/productionTimes';
import ErrorHelper from '../helpers/ErrorHelper';
import NavigationFunctionsMixin from '../mixins/navigation-functions';
import paintListCreatedDialog from '../components/dialogs/PaintListCreatedDialog';
import { showErrorMessage, showInfoMessage } from '../dialogs';
import workstationApi from '../api/workstation';
import ProductTimer from '../components/scanProducts/ProductTimer';
import { optimizedPaintListMocker } from '../shared/qrMockers';
import { createAggregatedTimer } from '../shared/timers';
import ErrorReportDialog from '../components/errorReports/ErrorReportDialog';
import localization from '../mixins/localization';

export default {
    mixins: [NavigationFunctionsMixin, localization],
    components: { ErrorReportDialog, Spinner, ProductTimer },
    data() {
        return {
            pausingOrRestarting: false,
            timer: null,
            loadingTimer: false,
            workstation: null,
            saving: false,
            loadingSawlines: false,
            loadingPaintlist: false,
            paintlistId: '',
            paintlist: null,
            paintlistrecord: null,
            printingProductionLabels: false,
            reporting: false,
        };
    },
    mounted() {
        workstationApi.fetchByCustomView('#sawmachine').then((response) => {
            this.workstation = response.data.data;
        });

        this.$bus.$on('barcode-scan', (data) => {
            this.paintlistId = data;
            this.loadData();
        });

        this.$bus.$on('qr_scanner.on_scan', (key, value) => {
            if (key === 'sawmachinepage') {
                this.onScan(value);
            }
        });
    },
    computed: {
        Config: () => Config,
        selectedPrinterPostParameter() {
            return this.$store.getters[
                'printers/getSelectedPrinterPostParameter'
            ]();
        },
        loading() {
            return (
                this.loadingPaintlist ||
                this.loadingSawlines ||
                this.loadingTimer
            );
        },
        allFinished() {
            if (!this.paintlist || !this.paintlist.records) {
                return false;
            }
            return this.paintlist.records.every((x) => x.sawlinesSawn == 1);
        },
        selectedSawlineIds() {
            if (!this.paintlistrecord || !this.paintlistrecord.sawlist) {
                return [];
            }
            return this.paintlistrecord.sawlist
                .filter((x) => x.selected)
                .map((x) => x.paintlistRecordSawlineId);
        },
    },
    beforeRouteLeave(to, from, next) {
        if (this.timer && !this.timer.finished) {
            this.onFinish(false);
        }
        next();
    },
    methods: {
        finish(pause) {
            const recordIds = this.timer.products.map(
                (x) => x.historyRecord.ID
            );
            return productionTimesApi
                .finishOptimizedPaintList(this.paintlistId, recordIds, pause)
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.sawmachine.unknown_error_saving')
                    );
                    this.timer.finished = false;
                });
        },
        onFinish(doRedirectToWorkpostsMenu = true) {
            this.saving = true;
            this.finish(false)
                .then(() => {
                    showInfoMessage(
                        this.trans('pages.sawmachine.production_time_saved')
                    );
                    if (doRedirectToWorkpostsMenu) {
                        this.$router.push('/workpostsmenu');
                    }
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        onPause() {
            this.pausingOrRestarting = true;
            this.finish(true)
                .then(() => {
                    showInfoMessage(
                        this.trans('pages.sawmachine.production_time_saved')
                    );
                })
                .finally(() => {
                    this.pausingOrRestarting = false;
                });
        },
        startTimer(restart = false) {
            if (restart) {
                this.pausingOrRestarting = true;
            } else {
                this.loadingTimer = true;
            }
            productionTimesApi
                .fetchOptimizedPaintList(this.paintlistId, this.workstation.id)
                .then((response) => {
                    const timerData = response.data.data;
                    if (timerData.missingMinutes.length > 0) {
                        const str = timerData.missingMinutes
                            .map((x) => '#' + x)
                            .join(', ');
                        const plural =
                            timerData.missingMinutes.length == 1 ? '' : 's';
                        showInfoMessage(
                            this.trans('pages.sawmachine.no_target_times', {
                                plural: plural,
                                types: str,
                            })
                        );
                    }
                    this.timer = createAggregatedTimer(timerData);
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.sawmachine.could_not_start_timer')
                    );
                })
                .finally(() => {
                    if (restart) {
                        this.pausingOrRestarting = false;
                    } else {
                        this.loadingTimer = false;
                    }
                });
        },
        scan() {
            this.$bus.$emit('qr_scanner.start', 'sawmachinepage', {
                singleScan: true,
                defaultCamera: 'front',
                mocker: optimizedPaintListMocker,
            });
        },
        onScan(value) {
            let decoded = null;
            try {
                decoded = JSON.parse(value);
            } catch (error) {}

            if (decoded && decoded.context === 'optimizedPaintList') {
                this.paintlistId = decoded.optimizedPaintListId;
                this.loadData();
            } else {
                showErrorMessage(
                    this.trans('pages.sawmachine.invalid_qr_code'),
                    null,
                    1500
                );
            }
        },
        loadData() {
            if (this.loadingPaintlist) {
                return;
            }

            if (this.paintlistId.toString().length == 0) {
                return showErrorMessage(
                    this.trans('pages.sawmachine.invalid_paintlist_number')
                );
            }

            this.loadingPaintlist = true;

            optimizedPaintListApi
                .fetch(this.paintlistId)
                .then((response) => {
                    const paintlist = response.data.data;
                    if (paintlist.reference === null) {
                        paintlist.reference = paintlist.ID;
                    }
                    this.paintlistId = paintlist.ID;
                    this.paintlist = paintlist;
                    this.paintlistrecord = null;
                    this.sortRecords();
                    this.startTimer();
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.sawmachine.could_not_load_paintlist')
                    );
                })
                .finally(() => {
                    this.loadingPaintlist = false;
                });
        },
        sortRecords() {
            this.paintlist.records.sort((a, b) => {
                if (a.sawlinesSawn !== b.sawlinesSawn) {
                    return a.sawlinesSawn - b.sawlinesSawn;
                }

                if (a.showPaintlist !== b.showPaintlist) {
                    return b.showPaintlist - a.showPaintlist;
                }

                if (a.profile !== b.profile) {
                    return a.profile - b.profile;
                }

                if (a.length !== b.length) {
                    return b.length - a.length;
                }

                return a.ID - b.ID;
            });
        },
        getMiter(sawline) {
            const leftEnd = parseInt(sawline.leftEnd);
            const rightEnd = parseInt(sawline.rightEnd);

            let miter = '';
            if (leftEnd == 0) {
                miter += '| ';
            } else if (leftEnd >= 1) {
                miter += '\\ ';
            }

            if (rightEnd == 0) {
                miter += '|';
            } else if (rightEnd >= 1) {
                miter += '/';
            }

            return miter;
        },
        showSawmachineSawlines(paintlistrecordId) {
            this.loadingSawlines = true;

            sawlistApi
                .fetchSawlinesForPaintlistrecordV2(paintlistrecordId)
                .then((response) => {
                    const record = response.data.data;
                    if (record.sawlist) {
                        record.sawlist.forEach((x) => {
                            x.selected = false;
                        });
                    }
                    this.paintlistrecord = record;
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.sawmachine.could_not_load_sawlines')
                    );
                })
                .finally(() => {
                    this.loadingSawlines = false;
                });
        },
        toggleSawmachineSawline(sawline) {
            sawlistApi
                .updateSawnForSawline(
                    sawline.paintlistRecordSawlineId,
                    sawline.sawn
                )
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.sawmachine.unknown_error')
                    );
                    sawline.sawn = sawline.sawn == 1 ? '0' : '1';
                })
                .finally(() => {
                    const recordLeft = this.paintlist.records.find(
                        (x) => x.ID == this.paintlistrecord.paintlistrecordId
                    );
                    recordLeft.sawlinesSawn = (
                        this.paintlistrecord.sawlist || []
                    ).every((x) => x.sawn == 1)
                        ? '1'
                        : '0';
                    this.sortRecords();
                });
        },
        onPrintProductLabels() {
            if (KeyHelper.isPressed('Shift')) {
                return window.open(
                    Config.url +
                        '/export/exportProductAndCncLabelsForOptimizedPaintlist/' +
                        '?optimizedPaintlistId=' +
                        this.paintlist.ID
                );
            }
            showInfoMessage(this.trans('pages.sawmachine.labels_printing'));
            this.printingProductionLabels = true;
            exportApi
                .printProductAndCncLabelsForOptimizedPaintlist(
                    this.paintlist.ID,
                    this.selectedPrinterPostParameter
                )
                .then(() => {
                    showInfoMessage(
                        this.trans('pages.sawmachine.labels_printed')
                    );
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.sawmachine.error_printing_labels')
                    );
                })
                .finally(() => {
                    this.printingProductionLabels = false;
                });
        },
        onPrintVLRLabel() {
            if (KeyHelper.isPressed('Shift')) {
                return window.open(
                    Config.url +
                        '/export/exportWindowLabelsForOptimizedPaintlist/' +
                        '?optimizedPaintlistId=' +
                        this.paintlist.ID +
                        '&employeeId=' +
                        this.$store.getters['currentUser/getUserId']
                );
            }

            exportApi
                .printWindowLabelsForOptimizedPaintlist(
                    this.paintlist.ID,
                    this.selectedPrinterPostParameter
                )
                .then(() => {
                    showInfoMessage(
                        this.trans('pages.sawmachine.all_vlr_labels_printing')
                    );
                });
        },
        onPrintVLRLabelForRecord() {
            if (!this.paintlistrecord) {
                return showInfoMessage(
                    this.trans('pages.sawmachine.choose_line')
                );
            }

            if (KeyHelper.isPressed('Shift')) {
                return window.open(
                    Config.url +
                        '/export/exportWindowLabelsForOptimizedPaintlistRecord/' +
                        '?optimizedPaintlistRecordId=' +
                        this.paintlistrecord.paintlistrecordId +
                        '&employeeId=' +
                        this.$store.getters['currentUser/getUserId']
                );
            }

            exportApi
                .printWindowLabelsForOptimizedPaintlistRecord(
                    this.paintlistrecord.paintlistrecordId,
                    this.selectedPrinterPostParameter
                )
                .then(() => {
                    showInfoMessage(
                        this.trans('pages.sawmachine.all_vlr_labels_printing')
                    );
                });
        },
        onLineToSawmachine() {
            if (!this.paintlistrecord) {
                return showInfoMessage(
                    this.trans('pages.sawmachine.choose_line')
                );
            }

            window.open(
                Config.url +
                    '/optimizedpaintlist/sendRecordToSawmachine/?recordId=' +
                    this.paintlistrecord['paintlistrecordId']
            );
        },
        createErrorReport(selectedOption) {
            this.reporting = true;

            sawlineApi
                .errorReport(
                    this.paintlist.ID,
                    this.selectedSawlineIds,
                    this.paintlistrecord.paintlistrecordId,
                    selectedOption,
                    this.$store.getters['currentUser/getUserId']
                )
                .then((response) => {
                    this.$refs.errorReportDialog.close();
                    const newPaintList = response.data.data;
                    paintListCreatedDialog(newPaintList)
                        .then(() => {
                            this.navigateToOptimizedPaintlistView(
                                newPaintList.id
                            );
                        })
                        .catch(() => {});
                })
                .catch((error) => {
                    const translations = {
                        'optimizedpaintlist.invalid': this.trans(
                            'pages.sawmachine.paintlist_does_not_exist'
                        ),
                        'sawlines.empty': this.trans(
                            'pages.sawmachine.select_sawlines'
                        ),
                        'invalid.sawline.id': this.trans(
                            'pages.sawmachine.invalid_sawline'
                        ),
                        'invalid.optimizedpaintlistrecord.id': this.trans(
                            'pages.sawmachine.invalid_paintlist_record'
                        ),
                    };

                    ErrorHelper.displayError(error, translations);
                })
                .finally(() => {
                    this.reporting = false;
                });
        },
    },
};
</script>
